import { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  useDispatch,
  useSelector,
  clearAfterCurrentCompanyChanges,
} from 'stores'
import { useService } from 'services'
import {
  fetchUserByToken,
  initialState,
  UserState,
  addCompanies,
  mergeLegalPersonWithCompanies,
  setCurrentCompany,
} from '../stores/detail.store'

import { Company, LegalPerson } from '@types'
import { URL_HOME } from 'constants/urls'
import {
  STORE_STATUS_FINISHED,
  STORE_STATUS_INITIALIZED,
} from 'constants/store'
import { useFeatureFlag } from 'modules/feature-flag'

const COMPANY_ID = 'tno-supplier-company-id'

export function useUser() {
  const { companyId, supplierCompanyId } = useParams<{
    companyId?: string
    supplierCompanyId?: string
  }>()
  const { user: service } = useService()

  const dispatch = useDispatch()
  const user = useSelector((state) => state.user.detail)
  const situation = useSelector((state) => state.user.situation)

  const router = useHistory()
  const { fetchFeatureFlag } = useFeatureFlag()

  useEffect(() => {
    if (
      situation.status === STORE_STATUS_FINISHED &&
      user.status === STORE_STATUS_FINISHED
    ) {
      dispatch(
        addCompanies(
          convertLegalPersonToCompany(situation.data.legal_person_situation)
        )
      )
    }
  }, [situation.status])

  function convertLegalPersonToCompany(
    legalPerson: LegalPerson[] = []
  ): Company[] {
    return [...legalPerson].map((lp) => ({
      id: lp.document_number,
      document_number: lp.document_number,
      document_type: lp.document_type,
      name: lp.name,
      is_buyer: true,
      is_legal_person: true,
    }))
  }

  function isUserReady() {
    return user.status === STORE_STATUS_FINISHED
  }

  function isLoading() {
    return user.status !== STORE_STATUS_FINISHED && !user.data.email
  }

  async function fetch(
    localCompanyId: string = companyId || supplierCompanyId || '',
    forceFetchFeatureFlag = false
  ) {
    if (user.status !== STORE_STATUS_INITIALIZED && !forceFetchFeatureFlag)
      return

    const fetch: Promise<UserState> = new Promise((res, rej) => {
      service
        .info()
        .then((response) => {
          // We need to merge legal_person_situation data with companies because of pre-approved credit limit.
          const companies = mergeLegalPersonWithCompanies(
            convertLegalPersonToCompany(situation.data.legal_person_situation),
            response.companies
          )

          const storedCompanyId = localStorage.getItem(COMPANY_ID)

          let currentCompany: Company | undefined = undefined

          // Get company from new id
          currentCompany = findCompany(companies, localCompanyId)

          if (!currentCompany && storedCompanyId) {
            currentCompany = findCompany(companies, storedCompanyId)
          }

          if (!currentCompany) {
            currentCompany = companies[0] || initialState.data.currentCompany
          }

          dispatch(setCurrentCompany(currentCompany))

          const payload = {
            ...response,
            companies,
            currentCompany,
          }

          // Store current company in local storage
          localStorage.setItem(COMPANY_ID, currentCompany.id)

          // TODO: Feature Flag is based on company id for now.
          fetchFeatureFlag(currentCompany)
            .then((features) => {
              res({
                ...payload,
                features,
                userValidatedByOwner: false,
              })
            })
            .catch(rej)
        })
        .catch(rej)
    })
    dispatch(fetchUserByToken(fetch))
  }

  function setCurrentById(companyId: string) {
    const currentCompany = getCurrent(user.data.companies, companyId)
    dispatch(setCurrentCompany(currentCompany))
    clearAfterCurrentCompanyChanges(dispatch)
    fetch(currentCompany.id, true)
    router.push(`${URL_HOME}/${companyId}`)
  }

  function findCompany(companies: Company[], companyId: string) {
    return companies.find((company) => company.id === companyId)
  }

  function getCurrent(companies: Company[], companyId?: string) {
    return (
      companies.find((company) => company.id === companyId) ||
      companies[0] ||
      initialState.data.currentCompany
    )
  }

  function mergeCompanies(companies: Company[]) {
    dispatch(addCompanies(companies))
  }

  return {
    user,
    situation,
    companyId: user.data.currentCompany.id,
    fetch,
    setCurrentById,
    isUserReady,
    mergeCompanies,
    isLoading,
  }
}
